/**
 * QorynHalo — the redesigned voice visualization.
 *
 * Root corrections (2026-09-25 remainder pass A/B/D):
 *   • RAF is now gated on **presentation eligibility** in addition to
 *     tab visibility / viewport / reduced-motion. Calm states
 *     (`idle` · `held` · `error` · `text_only` · `muted` ·
 *     `speaking_muted`) paint the polished static form ONCE and stop
 *     the loop. No inactive RAF, no unnecessary analyser reads.
 *   • Smoothed amplitude is cleared on ALL of:
 *       - `analyserRef.current` value change (session teardown /
 *         replacement),
 *       - transition to a non-motion-eligible presentation,
 *       - `sessionKey` change (session identity — resume can never
 *         reintroduce prior energy),
 *       - tab hidden / offscreen / reduced-motion becoming true.
 *   • The polished SVG static form is rendered ONLY as a fallback when
 *     the 2D context is unavailable. When the canvas is available, the
 *     canvas itself paints the static form for calm states (single
 *     paint) so there is no double-stacked geometry.
 *   • Ring geometry is bounded by `min(size * 0.42, 115)` so the
 *     visible ring diameter stays within the design target of
 *     200–230 px whenever space permits, while shrinking honestly on
 *     narrow consoles.
 *
 * Truth rules unchanged:
 *   • Amplitude only from `analyser.getByteTimeDomainData()` on the
 *     AnalyserNode wired to Qoryn's OUTPUT audio stream (true RMS).
 *   • AnalyserNode is passive — source-only, never `ctx.destination`.
 *   • Speaking + speaker mute → `speaking_muted` (no reactive term).
 *
 * Props:
 *   { presentation, analyserRef, sessionKey?, size?, showQMark?,
 *     dataTestId?, onFrame? }
 *
 *   `onFrame` is an optional test-only callback fired once per RAF
 *   tick with `{frame, presentation, smoothedRms, deformMagnitude,
 *   analyserRead}`.
 */

import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";

import { Q_OPACITY_BY_PRESENTATION, isAudioReactive } from "@/lib/haloPresentation";
import QorynQMark from "@/components/QorynQMark";

const RING_COUNT = 29;
const SEGMENTS = 128;

// Q asset sizes (main-inspected values).
const Q_SIZE_PX = 162;
const Q_DISC_PX = 80;

// Brand palette — violet → lavender → cyan.
const VIOLET = "#8B5CF6";
const LAVENDER = "#C4B5FD";
const CYAN = "#22D3EE";

// Presentations that carry genuine motion (audio-reactive or steady
// ambient drift). Others paint the static form once and stop the RAF.
const MOTION_ELIGIBLE = new Set([
    "listening",
    "thinking",
    "speaking",
    "connecting",
]);

// Ambient drift (radians / ms) per motion-eligible presentation.
const AMBIENT_DRIFT_BY_PRES = {
    listening: 0.16,
    thinking: 0.48,
    speaking: 0.28,
    connecting: 0.32,
};

function readPrefersReducedMotion() {
    try {
        return !!(
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        );
    } catch {
        return false;
    }
}

// Precomputed offset-contour vectors (golden-angle spiral, k=0..28).
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
function buildOffsets() {
    const out = new Array(RING_COUNT);
    for (let k = 0; k < RING_COUNT; k += 1) {
        const r = 0.11 * Math.sqrt(k / (RING_COUNT - 1));
        const theta = k * GOLDEN_ANGLE;
        out[k] = { dx: r * Math.cos(theta), dy: r * Math.sin(theta) };
    }
    return out;
}
const OFFSETS = buildOffsets();

// Compute visible ring base radius from the canvas size. Target visible
// diameter is ~200–230 px whenever space permits (Q = 162 must fit
// inside comfortably); on narrow consoles the ring shrinks with the
// wrapper honestly.
function computeRingRadius(size) {
    // 0.42 * size targets 230 diameter at 274 px canvas and larger; we
    // cap at 115 so wide canvases don't grow the ring beyond the
    // designed maximum.
    return Math.min(size * 0.42, 115);
}

function QorynHaloBase({
    presentation = "idle",
    analyserRef = null,
    sessionKey = null,
    size = 480,
    showQMark = true,
    dataTestId = "qoryn-halo",
    onFrame = null,
}) {
    const wrapperRef = useRef(null);
    const canvasRef = useRef(null);
    const rafRef = useRef(0);
    const presentationRef = useRef(presentation);
    const onFrameRef = useRef(onFrame);

    const [reducedMotion, setReducedMotion] = useState(() =>
        readPrefersReducedMotion(),
    );
    const [tabVisible, setTabVisible] = useState(() =>
        typeof document !== "undefined" ? !document.hidden : true,
    );
    const [inView, setInView] = useState(true);
    const [ctxAvailable, setCtxAvailable] = useState(true);

    // Analyser reference tracking (for reset on replacement).
    const analyserRefKeyRef = useRef(null);
    const smoothedRef = useRef({ rms: 0, hi: 0 });

    // Motion eligibility gate — recomputed on every prop change so the
    // effect's `shouldAnimate` reflects the CURRENT presentation.
    const motionEligible = MOTION_ELIGIBLE.has(presentation);
    const shouldAnimate =
        ctxAvailable &&
        !reducedMotion &&
        tabVisible &&
        inView &&
        motionEligible;
    const staticFallback = !ctxAvailable || reducedMotion;

    // Root remainder pass (paint-order defect):
    //   `presentationRef` and the smoothing reset MUST be synchronised
    //   BEFORE the canvas layout effect paints, so a static repaint on
    //   transition to `held` / `error` / etc. never reads the previous
    //   presentation. Declared as a `useLayoutEffect` earlier than the
    //   canvas effect below so React runs it first.
    //
    // Root remainder pass (smoothing reset regression):
    //   Reset smoothed amplitude on EVERY transition OUT of the
    //   audio-reactive presentation ("speaking"). The prior version
    //   only reset when entering a non-motion-eligible state, so
    //   `speaking → listening → speaking(silent)` could inherit prior
    //   loud RMS via 0.9-per-frame decay. Reset here is unconditional
    //   for any non-audio-reactive presentation.
    useLayoutEffect(() => {
        presentationRef.current = presentation;
        if (!isAudioReactive(presentation)) {
            smoothedRef.current = { rms: 0, hi: 0 };
        }
    }, [presentation]);

    // Also reset on gating transitions (hidden / offscreen / reduced-
    // motion), and on session identity change. Kept as separate layout
    // effects so React runs each one synchronously before the canvas
    // layout effect below.
    useLayoutEffect(() => {
        if (!tabVisible || !inView || reducedMotion) {
            smoothedRef.current = { rms: 0, hi: 0 };
        }
    }, [tabVisible, inView, reducedMotion]);
    useLayoutEffect(() => {
        smoothedRef.current = { rms: 0, hi: 0 };
    }, [sessionKey]);

    useEffect(() => {
        onFrameRef.current = onFrame;
    }, [onFrame]);

    // Dynamic reduced-motion listener.
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReducedMotion(mq.matches);
        const handler = (e) => setReducedMotion(e.matches);
        try {
            mq.addEventListener("change", handler);
        } catch {
            mq.addListener?.(handler);
        }
        return () => {
            try {
                mq.removeEventListener("change", handler);
            } catch {
                mq.removeListener?.(handler);
            }
        };
    }, []);

    useEffect(() => {
        if (typeof document === "undefined") return;
        const onVis = () => setTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
            return;
        }
        const el = wrapperRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) setInView(entry.isIntersecting);
            },
            { threshold: 0 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    // Canvas RAF loop — schedules ONLY when `shouldAnimate` is true.
    // Presentation is a dependency so a transition to a calm state
    // tears down the loop cleanly (a final static paint runs in the
    // effect body before returning).
    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            setCtxAvailable(false);
            return;
        }

        const dpr =
            typeof window !== "undefined"
                ? Math.min(window.devicePixelRatio || 1, 2)
                : 1;
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        canvas.style.width = `${size}px`;
        canvas.style.height = `${size}px`;

        let ctx = null;
        try {
            ctx = canvas.getContext("2d");
        } catch {
            ctx = null;
        }
        if (!ctx) {
            setCtxAvailable(false);
            return;
        }
        setCtxAvailable(true);
        ctx.scale(dpr, dpr);

        const w = size;
        const h = size;
        const cx = w / 2;
        const cy = h / 2;
        const R = computeRingRadius(size);

        const ringPhases = Array.from({ length: RING_COUNT }, (_, k) => ({
            phase: (k * 0.83) % (Math.PI * 2),
            freq: 3 + (k % 5),
            drift: 0.0004 + (k % 7) * 0.00011,
        }));

        let timeBuf = null;
        let freqBuf = null;
        let frameCounter = 0;

        const paintStatic = () => {
            ctx.clearRect(0, 0, w, h);
            drawAura(ctx, cx, cy, R, 0.14);
            const grad = makeContourGradient(ctx, cx, cy, R);
            for (let k = 0; k < RING_COUNT; k += 1) {
                const off = OFFSETS[k];
                const ocx = cx + off.dx * R;
                const ocy = cy + off.dy * R;
                const kn = k / (RING_COUNT - 1);
                ctx.beginPath();
                ctx.arc(ocx, ocy, R * (0.94 + kn * 0.02), 0, Math.PI * 2);
                ctx.strokeStyle = grad;
                ctx.globalAlpha =
                    (0.16 + kn * 0.35) *
                    (presentationRef.current === "error" ? 0.35 : 1.0) *
                    (presentationRef.current === "held" ? 0.5 : 1.0);
                ctx.lineWidth = 0.5 + kn * 0.35;
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
        };

        const paintDynamic = (now, dt) => {
            const p = presentationRef.current;
            const audioReactive = isAudioReactive(p);
            const ambientDrift = AMBIENT_DRIFT_BY_PRES[p] ?? 0;

            let analyserRead = false;
            const analyser = analyserRef?.current ?? null;
            if (analyser !== analyserRefKeyRef.current) {
                analyserRefKeyRef.current = analyser;
                smoothedRef.current = { rms: 0, hi: 0 };
                timeBuf = null;
                freqBuf = null;
            }

            if (
                audioReactive &&
                analyser &&
                typeof analyser.getByteTimeDomainData === "function"
            ) {
                const fft = analyser.fftSize ?? 512;
                if (!timeBuf || timeBuf.length !== fft) {
                    timeBuf = new Uint8Array(fft);
                }
                analyser.getByteTimeDomainData(timeBuf);
                let sum = 0;
                for (let i = 0; i < timeBuf.length; i += 1) {
                    const v = (timeBuf[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / timeBuf.length);
                let hi = 0;
                if (typeof analyser.getByteFrequencyData === "function") {
                    const bins = analyser.frequencyBinCount ?? 128;
                    if (!freqBuf || freqBuf.length !== bins) {
                        freqBuf = new Uint8Array(bins);
                    }
                    analyser.getByteFrequencyData(freqBuf);
                    const hiStart = Math.floor(bins * 0.55);
                    let hiSum = 0;
                    for (let i = hiStart; i < bins; i += 1) hiSum += freqBuf[i];
                    hi = hiSum / ((bins - hiStart || 1) * 255);
                }
                const S = smoothedRef.current;
                const attack = 0.4;
                const release = 0.18;
                S.rms =
                    rms > S.rms
                        ? S.rms + (rms - S.rms) * attack
                        : S.rms + (rms - S.rms) * release;
                S.hi =
                    hi > S.hi
                        ? S.hi + (hi - S.hi) * attack
                        : S.hi + (hi - S.hi) * release;
                analyserRead = true;
            } else {
                const S = smoothedRef.current;
                S.rms *= 0.9;
                S.hi *= 0.9;
            }

            const S = smoothedRef.current;
            // Bounded ring deformation: total contour excursion beyond
            // the base ring must fit inside the canvas margin at ALL
            // sizes, otherwise loud input clips against the rectangular
            // canvas edge on narrow consoles (272px).
            //   max radial extent of the ring family from canvas center
            //     = 0.11*R (spiral offset max)
            //     + R*0.96 (baseR at kn=1)
            //     + 0.9 * localDeform (|sin*0.55| + |sin*0.35|)
            //   where localDeform_max = deformMag * (0.18 + 1.15) = deformMag * 1.33
            //   → total wave extent = 1.197 * deformMag
            //   → constraint: 1.07*R + 1.197*deformMag <= min(cx,cy) - safety
            const availableMargin = Math.min(cx, cy) - R * 1.07;
            const maxDeformCanvas = Math.max(0, (availableMargin - 2) / 1.197);
            const rawDeform = audioReactive ? S.rms * 22 + S.hi * 6 : 0;
            const deformMag = Math.min(rawDeform, maxDeformCanvas);

            ctx.clearRect(0, 0, w, h);

            const auraAlpha = audioReactive
                ? 0.14 + Math.min(S.rms * 0.55, 0.45)
                : p === "thinking"
                  ? 0.16
                  : p === "listening"
                    ? 0.12
                    : p === "connecting"
                      ? 0.14
                      : 0.04;
            drawAura(ctx, cx, cy, R, auraAlpha);

            const grad = makeContourGradient(ctx, cx, cy, R);
            ctx.save();
            ctx.globalCompositeOperation = "lighter";
            for (let k = 0; k < RING_COUNT; k += 1) {
                const rp = ringPhases[k];
                rp.phase += rp.drift * dt * ambientDrift;

                const off = OFFSETS[k];
                const ocx = cx + off.dx * R;
                const ocy = cy + off.dy * R;
                const kn = k / (RING_COUNT - 1);
                const baseR = R * (0.94 + kn * 0.02);
                const localDeform = deformMag * (0.18 + kn * kn * 1.15);

                ctx.beginPath();
                for (let seg = 0; seg <= SEGMENTS; seg += 1) {
                    const a = (seg / SEGMENTS) * Math.PI * 2;
                    const w1 =
                        Math.sin(a * rp.freq + rp.phase) *
                        localDeform *
                        0.55;
                    const w2 =
                        Math.sin(a * (rp.freq + 2) - rp.phase * 1.6) *
                        localDeform *
                        0.35;
                    const rr = baseR + w1 + w2;
                    const x = ocx + Math.cos(a) * rr;
                    const y = ocy + Math.sin(a) * rr;
                    if (seg === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
                ctx.strokeStyle = grad;
                ctx.globalAlpha = 0.16 + kn * 0.5;
                ctx.lineWidth = 0.5 + kn * 0.4;
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
            ctx.restore();

            frameCounter += 1;
            if (onFrameRef.current) {
                try {
                    onFrameRef.current({
                        frame: frameCounter,
                        presentation: p,
                        smoothedRms: S.rms,
                        deformMagnitude: deformMag,
                        analyserRead,
                    });
                } catch {
                    /* callback errors must not break the loop */
                }
            }
        };

        // Always paint the static form once on effect entry so calm
        // states have a visible silhouette without an RAF loop.
        paintStatic();

        if (!shouldAnimate) {
            return () => {
                if (rafRef.current) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = 0;
                }
            };
        }

        let t0 = performance.now();
        const tick = (now) => {
            const dt = now - t0;
            t0 = now;
            paintDynamic(now, dt);
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = 0;
            }
        };
    }, [size, analyserRef, shouldAnimate, presentation]);

    return (
        <div
            ref={wrapperRef}
            data-testid={dataTestId}
            data-presentation={presentation}
            data-static={staticFallback ? "true" : "false"}
            data-animating={shouldAnimate ? "true" : "false"}
            className="relative flex items-center justify-center select-none"
            style={{
                width: size,
                height: size,
                pointerEvents: "none",
            }}
        >
            {ctxAvailable ? (
                <canvas
                    ref={canvasRef}
                    data-testid="qoryn-halo-canvas"
                    aria-hidden="true"
                    style={{
                        width: size,
                        height: size,
                        display: "block",
                    }}
                />
            ) : (
                // Polished SVG fallback — rendered ONLY when the 2D
                // context is unavailable. When the canvas is
                // available, the canvas itself paints the static form
                // for calm states so there is no double-stacked
                // geometry.
                <HaloStaticSvg size={size} />
            )}
            {showQMark && (
                <div
                    aria-hidden="true"
                    data-testid="qoryn-halo-center"
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ isolation: "isolate", pointerEvents: "none" }}
                >
                    <div
                        data-testid="qoryn-halo-q-disc"
                        style={{
                            position: "absolute",
                            width: Q_DISC_PX,
                            height: Q_DISC_PX,
                            borderRadius: "9999px",
                            backgroundColor: "#000000",
                            zIndex: 1,
                        }}
                    />
                    <div style={{ position: "relative", zIndex: 2 }}>
                        <QorynQMark
                            size={Q_SIZE_PX}
                            glow={presentation === "speaking" ? 1 : 0}
                            opacity={
                                Q_OPACITY_BY_PRESENTATION[presentation] ?? 1.0
                            }
                            testId="qoryn-halo-q-mark"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function makeContourGradient(ctx, cx, cy, R) {
    const g = ctx.createLinearGradient(cx, cy - R, cx, cy + R);
    g.addColorStop(0, VIOLET);
    g.addColorStop(0.5, LAVENDER);
    g.addColorStop(1, CYAN);
    return g;
}

function drawAura(ctx, cx, cy, R, alpha) {
    if (alpha <= 0) return;
    ctx.save();
    // Root polish (2026-09-25): the aura gradient outer radii must
    // stay INSIDE the nearest canvas edge so the gradient reaches
    // alpha 0 before it can clip against the rectangular canvas
    // border. Prior implementation used R*2.2 and R*1.4 unconditionally
    // — at size=272 (R≈114) both exceed the 136px canvas half-size and
    // produced a visible purple square behind the ring.
    const auraMax = Math.min(cx, cy);
    const g1Outer = Math.min(R * 2.2, auraMax);
    const g1Inner = Math.min(R * 0.2, g1Outer * 0.5);
    const g1 = ctx.createRadialGradient(cx, cy, g1Inner, cx, cy, g1Outer);
    g1.addColorStop(0, hexToRgba(VIOLET, alpha));
    g1.addColorStop(1, hexToRgba(VIOLET, 0));
    ctx.fillStyle = g1;
    ctx.beginPath();
    ctx.arc(cx, cy, g1Outer, 0, Math.PI * 2);
    ctx.fill();
    const g2Outer = Math.min(R * 1.4, auraMax);
    const g2Inner = Math.min(R * 0.9, g2Outer * 0.9);
    const g2 = ctx.createRadialGradient(cx, cy, g2Inner, cx, cy, g2Outer);
    g2.addColorStop(0, hexToRgba(CYAN, alpha * 0.7));
    g2.addColorStop(1, hexToRgba(CYAN, 0));
    ctx.fillStyle = g2;
    ctx.beginPath();
    ctx.arc(cx, cy, g2Outer, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

function hexToRgba(hex, alpha) {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

function HaloStaticSvg({ size }) {
    const cx = size / 2;
    const cy = size / 2;
    const R = computeRingRadius(size);
    const gradientId = `halo-static-grad-${size}`;
    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            aria-hidden="true"
            data-testid="qoryn-halo-static-svg"
            style={{ display: "block", width: size, height: size }}
        >
            <defs>
                <linearGradient
                    id={gradientId}
                    x1="0"
                    y1={cy - R}
                    x2="0"
                    y2={cy + R}
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0%" stopColor={VIOLET} />
                    <stop offset="50%" stopColor={LAVENDER} />
                    <stop offset="100%" stopColor={CYAN} />
                </linearGradient>
            </defs>
            {Array.from({ length: RING_COUNT }, (_, k) => {
                const off = OFFSETS[k];
                const ocx = cx + off.dx * R;
                const ocy = cy + off.dy * R;
                const kn = k / (RING_COUNT - 1);
                return (
                    <circle
                        key={k}
                        cx={ocx}
                        cy={ocy}
                        r={R * (0.94 + kn * 0.02)}
                        stroke={`url(#${gradientId})`}
                        strokeWidth={0.5 + kn * 0.35}
                        fill="none"
                        opacity={0.16 + kn * 0.35}
                    />
                );
            })}
        </svg>
    );
}

export const QorynHalo = memo(QorynHaloBase);
export default QorynHalo;
